import {
  addDoc,
  collectionGroup,
  collection,
  deleteDoc,
  deleteField,
  type DocumentData,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  startAfter,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  type QueryDocumentSnapshot,
  type QueryConstraint,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import {
  fromFirestoreWebinar,
  toFirestoreWebinarPatch,
} from '@core/services/webinars/converters/webinar-converter'
import { fromFirestoreWebinarCategory } from '@core/services/webinars/converters/webinar-category-converter'
import { WebinarErrorConverter } from '@core/services/webinars/converters/webinar-error-converter'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type {
  CreateWebinarInput,
  CreateWebinarQuestionInput,
  UpdateWebinarPatch,
  WebinarCategory,
  Webinar,
  WebinarQuestion,
} from '@domain/models/webinar'
import type { UserProfile } from '@domain/models/user'

const WEBINARS_COLLECTION = 'webinars'
const WEBINAR_CATEGORIES_COLLECTION = 'webinar_categories'
const WEBINAR_QUESTIONS_SUBCOLLECTION = 'questions'
const WEBINAR_QUESTION_LIKES_SUBCOLLECTION = 'likes'
const USERS_COLLECTION = 'users'
const MIN_PAST_WEBINARS_SCAN_BATCH_SIZE = 10
const MAX_PAST_WEBINARS_SCAN_BATCHES = 20

export interface PaginatedPastWebinarsResult {
  webinars: Webinar[]
  lastDoc: QueryDocumentSnapshot<DocumentData> | null
  hasMore: boolean
}

function isAdminUser(user: UserProfile): boolean {
  return user.admin === true || user.isAdmin === true
}

function assertAdminUser(currentUser: UserProfile, action: string): void {
  if (!currentUser.uid) {
    throw new CoreError({
      cause: CoreErrorCause.Forbidden,
      context: `No authenticated user available for ${action}.`,
    })
  }

  if (!isAdminUser(currentUser)) {
    throw new CoreError({
      cause: CoreErrorCause.PermissionDenied,
      context: `Current user is not allowed to ${action}.`,
    })
  }
}

function getWebinarsCollection() {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }
  return collection(firestore, WEBINARS_COLLECTION)
}

function getWebinarCategoriesCollection() {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }
  return collection(firestore, WEBINAR_CATEGORIES_COLLECTION)
}

function getWebinarDoc(webinarId: string) {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }
  return doc(firestore, WEBINARS_COLLECTION, webinarId)
}

function getUserDoc(userId: string) {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }
  return doc(firestore, USERS_COLLECTION, userId)
}

function getWebinarQuestionsCollection(webinarId: string) {
  return collection(getWebinarDoc(webinarId), WEBINAR_QUESTIONS_SUBCOLLECTION)
}

function getWebinarQuestionDoc(webinarId: string, questionId: string) {
  return doc(getWebinarQuestionsCollection(webinarId), questionId)
}

function getWebinarQuestionLikesCollection(webinarId: string, questionId: string) {
  return collection(
    getWebinarQuestionDoc(webinarId, questionId),
    WEBINAR_QUESTION_LIKES_SUBCOLLECTION,
  )
}

function getWebinarQuestionLikeDoc(
  webinarId: string,
  questionId: string,
  userId: string,
) {
  return doc(getWebinarQuestionLikesCollection(webinarId, questionId), userId)
}

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  return undefined
}

function hasYouTubeUrl(webinar: Webinar): boolean {
  return typeof webinar.youtubeUrl === 'string' && webinar.youtubeUrl.trim().length > 0
}

function normalizeCategoryId(webinarCategoryId?: string): string | undefined {
  const normalized = webinarCategoryId?.trim()
  return normalized && normalized.length > 0 ? normalized : undefined
}

function buildWebinarsByDateQuery(
  upcoming: boolean,
  nowDate: Date,
  limitCount?: number,
  webinarCategoryId?: string,
) {
  const normalizedCategoryId = normalizeCategoryId(webinarCategoryId)
  const constraints: QueryConstraint[] = [
    ...(normalizedCategoryId
      ? [where('webinar_category_id', '==', normalizedCategoryId)]
      : []),
    where('webinar_date', upcoming ? '>' : '<=', nowDate),
    orderBy('webinar_date', upcoming ? 'asc' : 'desc'),
  ]

  if (typeof limitCount === 'number' && limitCount > 0) {
    constraints.push(limit(limitCount))
  }

  return query(getWebinarsCollection(), ...constraints)
}

function buildPastWebinarsPaginatedQuery(
  pageSize: number,
  nowDate: Date,
  lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
  webinarCategoryId?: string,
) {
  const normalizedCategoryId = normalizeCategoryId(webinarCategoryId)
  const constraints: QueryConstraint[] = [
    ...(normalizedCategoryId
      ? [where('webinar_category_id', '==', normalizedCategoryId)]
      : []),
    where('webinar_date', '<=', nowDate),
    orderBy('webinar_date', 'desc'),
  ]

  if (lastDocument) {
    constraints.push(startAfter(lastDocument))
  }

  constraints.push(limit(pageSize))

  return query(getWebinarsCollection(), ...constraints)
}

interface PastWebinarScanResult {
  webinars: Webinar[]
  lastDoc: QueryDocumentSnapshot<DocumentData> | null
  hasMoreRawDocs: boolean
}

async function scanPastWebinarsWithYouTubeUrls(
  targetCount: number,
  nowDate: Date,
  webinarCategoryId?: string,
  lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
): Promise<PastWebinarScanResult> {
  const safeTargetCount = Math.max(1, targetCount)
  const batchSize = Math.max(
    MIN_PAST_WEBINARS_SCAN_BATCH_SIZE,
    safeTargetCount * 2,
  )

  let cursor = lastDocument ?? null
  let lastScannedDoc: QueryDocumentSnapshot<DocumentData> | null = null
  let hasMoreRawDocs = true
  let scannedBatches = 0
  const webinars: Webinar[] = []
  const seenWebinarIds = new Set<string>()

  while (
    webinars.length < safeTargetCount &&
    hasMoreRawDocs &&
    scannedBatches < MAX_PAST_WEBINARS_SCAN_BATCHES
  ) {
    const snapshot = await getDocs(
      buildPastWebinarsPaginatedQuery(
        batchSize,
        nowDate,
        cursor,
        webinarCategoryId,
      ),
    )
    scannedBatches += 1

    if (snapshot.empty) {
      hasMoreRawDocs = false
      break
    }

    lastScannedDoc = snapshot.docs[snapshot.docs.length - 1] ?? null
    cursor = lastScannedDoc
    hasMoreRawDocs = snapshot.docs.length >= batchSize

    const filteredWebinars = snapshot.docs
      .map((item) => fromFirestoreWebinar(item))
      .filter(hasYouTubeUrl)

    for (const webinar of filteredWebinars) {
      if (seenWebinarIds.has(webinar.uid)) {
        continue
      }
      seenWebinarIds.add(webinar.uid)
      webinars.push(webinar)
      if (webinars.length >= safeTargetCount) {
        break
      }
    }
  }

  return {
    webinars,
    lastDoc: lastScannedDoc,
    hasMoreRawDocs,
  }
}

export class WebinarsService {
  private static instance: WebinarsService

  private constructor() {}

  public static getInstance(): WebinarsService {
    if (!WebinarsService.instance) {
      WebinarsService.instance = new WebinarsService()
    }
    return WebinarsService.instance
  }

  public async createWebinar(
    input: CreateWebinarInput,
    currentUser: UserProfile,
  ): Promise<string> {
    assertAdminUser(currentUser, 'create webinars')

    try {
      const payload = toFirestoreWebinarPatch({
        title: input.title.trim(),
        webinarDate: input.webinarDate,
        webinarTime: input.webinarTime?.trim() || undefined,
        timeZone: input.timeZone?.trim() || undefined,
        durationMinutes: input.durationMinutes,
        youtubeUrl: input.youtubeUrl?.trim() || undefined,
        coverImageUrl: input.coverImageUrl?.trim() || undefined,
        webinarCategoryId: input.webinarCategoryId?.trim() || undefined,
        summary: input.summary?.trim() || undefined,
        registerUrl: input.registerUrl?.trim() || undefined,
        createdAt: new Date(),
      })

      const createdDoc = await addDoc(getWebinarsCollection(), payload)
      return createdDoc.id
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async updateWebinar(
    webinarId: string,
    patch: UpdateWebinarPatch,
    currentUser: UserProfile,
  ): Promise<void> {
    assertAdminUser(currentUser, 'manage webinars')

    try {
      const normalizedCoverImageUrl = patch.coverImageUrl?.trim()
      const payload = toFirestoreWebinarPatch({
        title: patch.title?.trim(),
        webinarDate: patch.webinarDate,
        webinarTime: patch.webinarTime?.trim(),
        timeZone: patch.timeZone?.trim(),
        durationMinutes: patch.durationMinutes,
        youtubeUrl: patch.youtubeUrl?.trim(),
        coverImageUrl: normalizedCoverImageUrl,
        webinarCategoryId: patch.webinarCategoryId?.trim(),
        summary: patch.summary?.trim(),
        registerUrl: patch.registerUrl?.trim(),
      })

      if (patch.coverImageUrl !== undefined && !normalizedCoverImageUrl) {
        payload['cover_image_url'] = deleteField()
      }

      if (Object.keys(payload).length === 0) {
        return
      }

      await updateDoc(getWebinarDoc(webinarId), payload)
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async deleteWebinar(
    webinarId: string,
    currentUser: UserProfile,
  ): Promise<void> {
    assertAdminUser(currentUser, 'delete webinars')

    try {
      await deleteDoc(getWebinarDoc(webinarId))
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async fetchWebinarCategories(activeOnly = true): Promise<WebinarCategory[]> {
    try {
      const categoriesQuery = query(getWebinarCategoriesCollection(), orderBy('name', 'asc'))
      const snapshot = await getDocs(categoriesQuery)
      const categories = snapshot.docs.map((item) => fromFirestoreWebinarCategory(item))
      return activeOnly ? categories.filter((category) => category.isActive) : categories
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async fetchWebinar(webinarId: string): Promise<Webinar> {
    try {
      const snapshot = await getDoc(getWebinarDoc(webinarId))
      if (!snapshot.exists()) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: `Webinar "${webinarId}" was not found.`,
        })
      }
      return fromFirestoreWebinar(snapshot)
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async fetchLatestPastWebinars(
    count = 4,
    nowDate = new Date(),
    webinarCategoryId?: string,
  ): Promise<Webinar[]> {
    try {
      const { webinars } = await scanPastWebinarsWithYouTubeUrls(
        count,
        nowDate,
        webinarCategoryId,
      )
      return webinars
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async fetchUpcomingWebinars(
    count = 2,
    nowDate = new Date(),
    webinarCategoryId?: string,
  ): Promise<Webinar[]> {
    try {
      const snapshot = await getDocs(
        buildWebinarsByDateQuery(true, nowDate, count, webinarCategoryId),
      )
      return snapshot.docs.map((item) => fromFirestoreWebinar(item))
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async fetchAllPastWebinars(
    nowDate = new Date(),
    webinarCategoryId?: string,
  ): Promise<Webinar[]> {
    try {
      const snapshot = await getDocs(
        buildWebinarsByDateQuery(false, nowDate, undefined, webinarCategoryId),
      )
      return snapshot.docs
        .map((item) => fromFirestoreWebinar(item))
        .filter(hasYouTubeUrl)
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async fetchAllUpcomingWebinars(
    nowDate = new Date(),
    webinarCategoryId?: string,
  ): Promise<Webinar[]> {
    try {
      const snapshot = await getDocs(
        buildWebinarsByDateQuery(true, nowDate, undefined, webinarCategoryId),
      )
      return snapshot.docs.map((item) => fromFirestoreWebinar(item))
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async fetchPastWebinarsPaginated(
    pageSize: number,
    lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
    nowDate = new Date(),
    webinarCategoryId?: string,
  ): Promise<PaginatedPastWebinarsResult> {
    try {
      const safePageSize = pageSize > 0 ? pageSize : 10
      const scanResult = await scanPastWebinarsWithYouTubeUrls(
        safePageSize,
        nowDate,
        webinarCategoryId,
        lastDocument,
      )

      return {
        webinars: scanResult.webinars,
        lastDoc: scanResult.lastDoc,
        hasMore: scanResult.hasMoreRawDocs,
      }
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async fetchWebinarByYouTubeUrl(youtubeUrl: string): Promise<Webinar | null> {
    const normalizedYouTubeUrl = youtubeUrl.trim()
    if (!normalizedYouTubeUrl) {
      return null
    }

    try {
      const lookupQuery = query(
        getWebinarsCollection(),
        where('youtube_url', '==', normalizedYouTubeUrl),
        limit(1),
      )
      const snapshot = await getDocs(lookupQuery)
      const firstMatch = snapshot.docs[0]
      return firstMatch ? fromFirestoreWebinar(firstMatch) : null
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async createWebinarQuestion(
    webinarId: string,
    input: CreateWebinarQuestionInput,
    currentUser: UserProfile,
  ): Promise<string> {
    if (!currentUser.uid) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: 'No authenticated user available for webinar questions.',
      })
    }

    try {
      const payload = {
        question: input.question.trim(),
        submitted_on: new Date(),
        submitted_by_uid: currentUser.uid,
        submitted_by_name: currentUser.profile_slug || null,
        submitted_by_ref: getUserDoc(currentUser.uid),
        likes_count: 0,
      }
      const createdDoc = await addDoc(getWebinarQuestionsCollection(webinarId), payload)
      return createdDoc.id
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  private async loadLikedQuestionIdsForUser(
    webinarId: string,
    userId: string,
  ): Promise<Set<string>> {
    if (!firestore) {
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Firestore is not initialized.',
      })
    }

    const likesQuery = query(
      collectionGroup(firestore, WEBINAR_QUESTION_LIKES_SUBCOLLECTION),
      where('webinar_id', '==', webinarId),
      where('user_uid', '==', userId),
    )

    const likesSnapshot = await getDocs(likesQuery)
    const likedQuestionIds = likesSnapshot.docs
      .map((item) => {
        const data = item.data() as Record<string, unknown>
        return typeof data['question_id'] === 'string' ? data['question_id'] : null
      })
      .filter((questionId): questionId is string => Boolean(questionId))

    return new Set(likedQuestionIds)
  }

  public async toggleWebinarQuestionLike(
    webinarId: string,
    questionId: string,
    userId: string,
  ): Promise<boolean> {
    if (!firestore) {
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Firestore is not initialized.',
      })
    }

    try {
      return await runTransaction(firestore, async (transaction) => {
        const questionDocRef = getWebinarQuestionDoc(webinarId, questionId)
        const likeDocRef = getWebinarQuestionLikeDoc(webinarId, questionId, userId)

        const questionSnapshot = await transaction.get(questionDocRef)
        if (!questionSnapshot.exists()) {
          throw new CoreError({
            cause: CoreErrorCause.DataNotFound,
            context: `Webinar question "${questionId}" was not found.`,
          })
        }

        const likeSnapshot = await transaction.get(likeDocRef)
        const questionData = questionSnapshot.data() as Record<string, unknown>
        const currentLikes =
          typeof questionData['likes_count'] === 'number'
            ? Math.max(0, questionData['likes_count'])
            : 0

        if (likeSnapshot.exists()) {
          transaction.delete(likeDocRef)
          transaction.update(questionDocRef, {
            likes_count: Math.max(0, currentLikes - 1),
          })
          return false
        }

        transaction.set(likeDocRef, {
          user_uid: userId,
          question_id: questionId,
          webinar_id: webinarId,
          liked_on: new Date(),
        })
        transaction.update(questionDocRef, {
          likes_count: currentLikes + 1,
        })
        return true
      })
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async deleteWebinarQuestion(webinarId: string, questionId: string): Promise<void> {
    if (!firestore) {
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Firestore is not initialized.',
      })
    }

    try {
      const likesCollection = getWebinarQuestionLikesCollection(webinarId, questionId)
      const deleteBatchSize = 400

      while (true) {
        const likesSnapshot = await getDocs(query(likesCollection, limit(deleteBatchSize)))
        if (likesSnapshot.empty) {
          break
        }

        const batch = writeBatch(firestore)
        likesSnapshot.docs.forEach((likeDoc) => {
          batch.delete(likeDoc.ref)
        })
        await batch.commit()

        if (likesSnapshot.size < deleteBatchSize) {
          break
        }
      }

      await deleteDoc(getWebinarQuestionDoc(webinarId, questionId))
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }

  public async fetchWebinarQuestions(
    webinarId: string,
    currentUserId?: string,
  ): Promise<WebinarQuestion[]> {
    try {
      const questionsQuery = query(
        getWebinarQuestionsCollection(webinarId),
        orderBy('likes_count', 'desc'),
        orderBy('submitted_on', 'desc'),
      )
      const snapshot = await getDocs(questionsQuery)
      const likedQuestionIds = currentUserId
        ? await this.loadLikedQuestionIdsForUser(webinarId, currentUserId)
        : new Set<string>()

      return snapshot.docs.map((item) => {
        const data = item.data() as Record<string, unknown>
        return {
          uid: item.id,
          question: typeof data['question'] === 'string' ? data['question'] : '',
          submittedOn: toOptionalDate(data['submitted_on']) ?? new Date(0),
          submittedByUid:
            typeof data['submitted_by_uid'] === 'string'
              ? data['submitted_by_uid']
              : '',
          submittedByName:
            typeof data['submitted_by_name'] === 'string'
              ? data['submitted_by_name']
              : undefined,
          likesCount:
            typeof data['likes_count'] === 'number' ? Math.max(0, data['likes_count']) : 0,
          likedByCurrentUser: likedQuestionIds.has(item.id),
        }
      })
    } catch (error) {
      throw WebinarErrorConverter.domainModel(error)
    }
  }
}

const webinarsService = WebinarsService.getInstance()

export async function createWebinar(
  input: CreateWebinarInput,
  currentUser: UserProfile,
): Promise<string> {
  return webinarsService.createWebinar(input, currentUser)
}

export async function updateWebinar(
  webinarId: string,
  patch: UpdateWebinarPatch,
  currentUser: UserProfile,
): Promise<void> {
  return webinarsService.updateWebinar(webinarId, patch, currentUser)
}

export async function deleteWebinar(
  webinarId: string,
  currentUser: UserProfile,
): Promise<void> {
  return webinarsService.deleteWebinar(webinarId, currentUser)
}

export async function fetchWebinar(webinarId: string): Promise<Webinar> {
  return webinarsService.fetchWebinar(webinarId)
}

export async function fetchWebinarCategories(
  activeOnly = true,
): Promise<WebinarCategory[]> {
  return webinarsService.fetchWebinarCategories(activeOnly)
}

export async function fetchLatestPastWebinars(
  count = 4,
  nowDate = new Date(),
  webinarCategoryId?: string,
): Promise<Webinar[]> {
  return webinarsService.fetchLatestPastWebinars(count, nowDate, webinarCategoryId)
}

export async function fetchUpcomingWebinars(
  count = 2,
  nowDate = new Date(),
  webinarCategoryId?: string,
): Promise<Webinar[]> {
  return webinarsService.fetchUpcomingWebinars(count, nowDate, webinarCategoryId)
}

export async function fetchAllPastWebinars(
  nowDate = new Date(),
  webinarCategoryId?: string,
): Promise<Webinar[]> {
  return webinarsService.fetchAllPastWebinars(nowDate, webinarCategoryId)
}

export async function fetchAllUpcomingWebinars(
  nowDate = new Date(),
  webinarCategoryId?: string,
): Promise<Webinar[]> {
  return webinarsService.fetchAllUpcomingWebinars(nowDate, webinarCategoryId)
}

export async function fetchPastWebinarsPaginated(
  pageSize: number,
  lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
  nowDate = new Date(),
  webinarCategoryId?: string,
): Promise<PaginatedPastWebinarsResult> {
  return webinarsService.fetchPastWebinarsPaginated(
    pageSize,
    lastDocument,
    nowDate,
    webinarCategoryId,
  )
}

export async function fetchWebinarByYouTubeUrl(
  youtubeUrl: string,
): Promise<Webinar | null> {
  return webinarsService.fetchWebinarByYouTubeUrl(youtubeUrl)
}

export async function createWebinarQuestion(
  webinarId: string,
  input: CreateWebinarQuestionInput,
  currentUser: UserProfile,
): Promise<string> {
  return webinarsService.createWebinarQuestion(webinarId, input, currentUser)
}

export async function fetchWebinarQuestions(
  webinarId: string,
  currentUserId?: string,
): Promise<WebinarQuestion[]> {
  return webinarsService.fetchWebinarQuestions(webinarId, currentUserId)
}

export async function toggleWebinarQuestionLike(
  webinarId: string,
  questionId: string,
  userId: string,
): Promise<boolean> {
  return webinarsService.toggleWebinarQuestionLike(webinarId, questionId, userId)
}

export async function deleteWebinarQuestion(
  webinarId: string,
  questionId: string,
): Promise<void> {
  return webinarsService.deleteWebinarQuestion(webinarId, questionId)
}
